import { Sparkle } from "@phosphor-icons/react";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { SEASONAL, PRIVATE_EVENTS, KIDS_CLASS, FLOWER_GROUP, FRENCHIE_GROUP, GALENTINES_SOPHIA, BANNER_GROUP, TESTIMONIALS } from "@/data/content";

const GALLERY = [
  FLOWER_GROUP,
  KIDS_CLASS,
  GALENTINES_SOPHIA,
  BANNER_GROUP,
  FRENCHIE_GROUP,
];

export default function Community() {
  return (
    <div data-testid="page-community" className="pb-10">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 md:pt-28">
        <SectionLabel className="mb-8">Community · Our Circle</SectionLabel>
        <FadeUp>
          <h1 className="font-serif text-[12vw] md:text-[8vw] leading-[0.92] tracking-tight">
            A circle of<br />
            <span className="font-serif-italic text-teal">extraordinary</span> women.
          </h1>
        </FadeUp>
        <FadeUp delay={0.15} className="mt-10 max-w-2xl font-light text-lg leading-relaxed text-charcoal/75">
          Flowtastics is a feel-good community for everyone — moving together, giving back, and connecting through purpose.
        </FadeUp>
      </section>

      {/* Regular Classes */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-24 md:mt-32" data-testid="community-regular-classes">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <FadeUp className="lg:col-span-5">
            <SectionLabel className="mb-6">Regular Classes</SectionLabel>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tight leading-[1.02]">
              Want to come to my <span className="font-serif-italic text-teal">regular classes?</span>
            </h2>
            <p className="font-light text-lg leading-relaxed text-charcoal/75 mt-8 max-w-md">
              Find me on the schedule at these studios — drop in for a flow any time.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-7">
            <div className="space-y-4">
              {[
                {
                  name: "CorePower Newton",
                  location: "Newton, MA",
                  href: "https://www.corepoweryoga.com/yoga-schedules/studio?instructorId=brand-clarity-10",
                },
                {
                  name: "Juno Arlington",
                  location: "Arlington, MA",
                  href: "https://junospace.com/schedule/",
                },
                {
                  name: "Tree Yoga",
                  location: "West Roxbury, MA",
                  href: "https://www.tree-yoga-west-roxbury.com/register-for-classes",
                },
              ].map((s, i) => (
                <FadeUp
                  key={s.name}
                  delay={i * 0.06}
                  className="border border-charcoal/15 rounded-3xl p-7 hover:border-teal hover:bg-blush/20 transition-all duration-500 group"
                  data-testid={`regular-class-${s.name.replace(/[^a-z]/gi, "").toLowerCase()}`}
                >
                  <a href={s.href} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-6">
                    <div>
                      <div className="text-[10px] uppercase tracking-editorial text-gold mb-3">Studio · 0{i + 1}</div>
                      <h3 className="font-serif text-3xl">{s.name}</h3>
                      <p className="font-light text-charcoal/60 mt-2 text-sm">{s.location}</p>
                    </div>
                    <span className="text-[11px] uppercase tracking-editorial text-charcoal/70 group-hover:text-teal transition-colors whitespace-nowrap">
                      View schedule →
                    </span>
                  </a>
                </FadeUp>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Quote */}
      <section className="max-w-[1100px] mx-auto px-6 mt-28 md:mt-40 text-center">
        <FadeUp>
          <Sparkle size={28} weight="light" className="text-gold mx-auto mb-6" />
          <p className="font-serif italic text-4xl md:text-6xl leading-[1.05]">
            "From Bachelorette weekends to Birthday bashes, Flowtastics brings the vibe, the energy, and the FUN to every event."
          </p>
        </FadeUp>
      </section>

      {/* Gallery */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-28" data-testid="community-gallery">
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-6 [column-fill:_balance]">
          {GALLERY.map((src, i) => (
            <FadeUp key={i} delay={i * 0.06} className="break-inside-avoid mb-4 md:mb-6 overflow-hidden rounded-3xl bg-blush/20 border border-blush/40">
              <img src={src} alt="" loading="lazy" className="w-full h-auto block hover:scale-105 transition-transform duration-[1.4s]" />
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Our Flows — seasonal feature */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 mt-16 md:mt-20" data-testid="community-seasonal">
        <FadeUp className="rounded-3xl overflow-hidden bg-blush/30 border-2 border-blush">
          <img src={SEASONAL} alt="Our Flows — Summer, Fall, Winter, Spring" className="w-full h-auto block" />
        </FadeUp>
      </section>

      {/* Testimonials full */}
      <section className="bg-blush/30 grain py-28 md:py-36 mt-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <FadeUp>
            <SectionLabel className="mb-6">Testimonials</SectionLabel>
            <h2 className="font-serif text-5xl md:text-7xl tracking-tight max-w-3xl leading-[1.02]">
              Voices from the <span className="font-serif-italic text-teal">mat.</span>
            </h2>
          </FadeUp>
          <div className="grid md:grid-cols-2 gap-10 md:gap-14 mt-16">
            {TESTIMONIALS.map((t, i) => (
              <FadeUp key={i} delay={i * 0.08} className="bg-cream rounded-3xl p-8 md:p-10">
                <Sparkle size={20} weight="light" className="text-gold mb-5" />
                <p className="font-serif italic text-2xl md:text-3xl leading-snug">"{t.quote}"</p>
                <div className="mt-8 pt-6 border-t border-charcoal/10 flex items-center justify-between text-[10px] uppercase tracking-editorial">
                  <span>{t.name}</span><span className="text-charcoal/50">{t.detail}</span>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Private Events */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-28 md:mt-40 space-y-12" data-testid="community-private-events">
        <FadeUp>
          <div className="max-w-[1100px] mx-auto rounded-3xl overflow-hidden bg-blush/30 border-2 border-blush">
            <img src={PRIVATE_EVENTS} alt="We also host private events" className="w-full h-auto block" />
          </div>
        </FadeUp>
        <FadeUp delay={0.1} className="max-w-3xl mx-auto text-center">
          <SectionLabel className="mb-6 inline-flex justify-center">Private Events</SectionLabel>
          <h2 className="font-serif text-5xl md:text-6xl tracking-tight leading-[1.02]">
            Your moment.<br /><span className="font-serif-italic text-teal">Your flow.</span>
          </h2>
          <p className="text-charcoal/75 font-light text-lg leading-relaxed mt-8 mx-auto">
            From bachelorette weekends to birthday bashes — Flowtastics brings the vibe, the energy, and the fun to every event. Scheduling is flexible. Every detail — your music, your gear, your favorite color combos — is tailored to you.
          </p>
          <p className="font-serif italic text-2xl text-teal mt-6">Always ready for any excuse to join the party.</p>
        </FadeUp>
      </section>
    </div>
  );
}
